import type { UIGameState } from '@sorrem-client/types';
import { LoadingPage } from '@sorrem-client/ui/features/LoadingPage';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { isShallowEqual, useGameSelector, useInputBindingPress, useSecondsUntil } from '@sorrem-client/ui/hooks';
import { useState } from 'react';
import {
    GameOverlayConnectionNotices,
    GameOverlayHud,
    RunHintBanner,
    RunHudTour,
} from './components';
import styles from './GameOverlay.module.scss';
import { useQuitCrucible, useRunLoadingGate, useSyncCameraZoom, useSyncDisplaySettings } from './hooks';
import { useNoticeToasts } from './use-notice-toasts';

/**
 * Fields from UIGameState that the overlay root renders. Fast-changing ones
 * (game time, party) are read by leaf components instead, so they don't
 * re-render the whole HUD.
 */
function toOverlayChromeState(s: UIGameState): Pick<UIGameState, 'runResult' | 'isConnectionLost'> {
    return {
        runResult: s.runResult,
        isConnectionLost: s.isConnectionLost,
    };
}

/** The countdown before the run comes alive. It ticks its own clock, so the HUD doesn't re-render with it. */
function GameOverlayCountdownBanner({ countdownEndsAtMs }: { readonly countdownEndsAtMs: number }): JSX.Element {
    const remainingSeconds = useSecondsUntil(countdownEndsAtMs) ?? 0;
    return <div className={styles.statusBanner}>{`The Crucible is coming alive in ${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}`}</div>;
}

interface GameOverlayProps {
    /**
     * Where the loading bar starts, 0 to 1: the share of the way into the
     * crucible already loaded before the run took over (a direct-entry run's
     * page preloads it), so the bar carries on rather than starting over.
     */
    readonly loadingProgressStart?: number;
    /** The party leader's account, to crown them on the party panel; unknown outside a gathered run. */
    readonly partyLeaderAccountId?: string | null;
}

/** Shows the run's notices as toasts. A leaf of its own, so a new notice doesn't re-render the HUD. */
function GameOverlayNoticeToasts(): null {
    useNoticeToasts();
    return null;
}

export function GameOverlay({ loadingProgressStart = 0, partyLeaderAccountId = null }: GameOverlayProps): JSX.Element {
    const { bridge, debugControls } = useGameplay();
    useSyncDisplaySettings();
    useSyncCameraZoom();
    const { runResult, isConnectionLost } = useGameSelector(bridge, toOverlayChromeState, isShallowEqual);
    const hasRunEnded = runResult !== null;
    const isDebugPreview = debugControls !== undefined;
    const {
        isLoadingScreenShown,
        loadingScreenProgress,
        waitingMessage,
        isHudShown,
        countdownEndsAtMs,
        hasCrucibleSession,
    } = useRunLoadingGate(loadingProgressStart);
    const { quit, isQuitting, quitError, dismissQuitError } = useQuitCrucible(hasRunEnded);

    // Esc dismisses what the carried panel or loadout has open first, and
    // only then opens the quit prompt. The open-menu binding only fires while
    // the game (or nothing) has focus, so pressing it inside an open dialog
    // closes that dialog instead.
    const [hasInventoryDismissable, setHasInventoryDismissable] = useState(false);
    const canQuitFromMenuBinding = !isDebugPreview && !hasRunEnded && !isConnectionLost && !isQuitting && !hasInventoryDismissable;
    useInputBindingPress('open-menu', canQuitFromMenuBinding ? (): void => { void quit(); } : undefined);

    return (
        <>
            <GameOverlayNoticeToasts />
            {isLoadingScreenShown && (
                <LoadingPage progress={loadingScreenProgress}>
                    {waitingMessage ?? undefined}
                </LoadingPage>
            )}
            <GameOverlayConnectionNotices />
            {countdownEndsAtMs !== null && (
                <GameOverlayCountdownBanner countdownEndsAtMs={countdownEndsAtMs} />
            )}
            {isHudShown && (
                <GameOverlayHud
                    hasCrucibleSession={hasCrucibleSession}
                    partyLeaderAccountId={partyLeaderAccountId}
                    hasRunEnded={hasRunEnded}
                    quitError={quitError}
                    onDismissQuitError={dismissQuitError}
                    onInventoryDismissableChange={setHasInventoryDismissable}
                />
            )}
            {isHudShown && <RunHintBanner />}
            {isHudShown && !isDebugPreview && <RunHudTour />}
        </>
    );
}
